import TemplateView from '../../../../TemplateView'
import EffectiveDateCell from '../workSchedule/EffectiveDateCell'
import messageResource from '../../../../messageResource'
import DeleteConfirmationCell from '../DeleteConfirmationCell'
import RateCell from './RateCell'
import LeaveRatesCell from './LeaveRatesCell'
import {isLeaveRatesEmpty, isRateEmpty, isRateValid, isValueEmpty} from './validationUtils'
import FloatNumberValidator from '../../../../administration/userManagement/ratesEditor/FloatNumberValidator'
import {ShowWarning, UserRatesErrors} from './constants'
import {ValidationErrors} from '../../constants'

export default class UserRatesRow extends TemplateView {
    constructor({
        readOnly = false,
        scrollTo = () => {},
        activeLeaveTypes,
        onChangeCb = () => {},
        onDeleteCb = () => {},
        rollbackLastEditedUserRateChanges = () => {},
        subscribeSelectorOnScroll = () => {},
        leaveRatesMenu,
    }) {
        super()

        this.activeLeaveTypes = activeLeaveTypes

        this.params = {
            readOnly,
            scrollTo,
            leaveRatesMenu,
        }

        this.callbacks = {
            onChangeCb,
            onDeleteCb,
            rollbackLastEditedUserRateChanges,
            subscribeSelectorOnScroll,
        }

        this.state = {
            rate: {
                rateId: null,
                effectiveDate: null,
                regularRate: null,
                overtimeRate: null,
                leaveRates: null
            },

            overtimeEnabled: false,
            leavesEnabled: false,
            activeLeaveTypes: [],
            overtimeRateCoefficient: 0,
            invertedDays: null,
            weekStartDay: 0,
            defaultWorkSchedule: [],
            effective: false,
            duplicateDate: false,
            error: {}
        }

        this.onStateChanged(() => this._update())

        this.onStatePropertyDeeplyChanged('error', error => this._updateError(error))
    }

    templateHtml() {
        return '<div class="userRatesRow">' +
               '    <div class="userRates">' +
               '        <div class="effectiveDateCellPlaceholder"/>' +
               '        <div class="regularRateColumn"/>' +
               '        <div class="overtimeRateColumn"/>' +
               '        <div class="leaveRatesColumn"/>' +
               '        <div class="deleteButtonCell">' +
               '            <div class="deleteIcon"/>' +
               '        </div>' +
               '    </div>' +
               '    <div class="deleteConfirmationContainer"/>' +
               '</div>'
    }

    afterRender() {
        this.$userRates = this.find('.userRates')

        this.effectiveDate = new EffectiveDateCell({
            dateFromLabel: messageResource.getMessage('user.add_edit.js.rates_editor.effective_form.edit'),
            scrollTo: this.params.scrollTo,
            onChangeCb: effectiveDate => this._handleRatesChanged({effectiveDate}),
            rollbackChanges: this.callbacks.rollbackLastEditedUserRateChanges,
            subscribeSelectorOnScroll: this.callbacks.subscribeSelectorOnScroll,
            readOnly: this.params.readOnly,
        }).init(this.find('.effectiveDateCellPlaceholder'))

        this.regularRate = new RateCell({
            readOnly: this.params.readOnly,
            scrollTo: this.params.scrollTo,
            onChangeCb: regularRate => this._handleRegularRateChanged(regularRate),
            rollbackChanges: this.callbacks.rollbackLastEditedUserRateChanges,
        }).init(this.find('.regularRateColumn'))

        this.$overtimeRateColumn = this.find('.overtimeRateColumn')

        this.overtimeRate = new RateCell({
            readOnly: this.params.readOnly,
            scrollTo: this.params.scrollTo,
            onChangeCb: overtimeRate => this._handleRatesChanged({overtimeRate}),
            rollbackChanges: this.callbacks.rollbackLastEditedUserRateChanges,
        }).init(this.$overtimeRateColumn)

        this.$leaveRatesColumn = this.find('.leaveRatesColumn')

        if (this.params.readOnly) {
            this.find('.deleteButtonCell').hide()
        } else {
            this.find('.deleteIcon').click(e => {
                e.stopPropagation()

                if (this.state.rate.rateId === -1) {
                    this.callbacks.onDeleteCb({removePermanently: true})
                } else {
                    this._toggleDeleteConfirmation(true)
                }
            })

            this.$deleteConfirmationContainer = this.find('.deleteConfirmationContainer').hide()
        }
    }

    _update() {
        this.$userRates.toggleClass('effective', this.state.effective)

        this.effectiveDate.setState({
            invertedDays: this.state.invertedDays,
            weekStartDay: this.state.weekStartDay,
            defaultWorkSchedule: this.state.defaultWorkSchedule,
            date: this.state.rate.effectiveDate,
            effective: this.state.effective,
        })

        this.regularRate.setState({
            rate: this.state.rate.regularRate,
        })

        this.$overtimeRateColumn.toggle(this.state.overtimeEnabled)

        if (this.state.overtimeEnabled) {
            this.overtimeRate.setState({
                rate: this.state.rate.overtimeRate,
            })
        }

        this.$leaveRatesColumn.toggle(this.state.leavesEnabled && this.state.activeLeaveTypes.length > 0)

        if (this.state.leavesEnabled && this.state.activeLeaveTypes.length > 0) {
            this.$leaveRatesColumn.empty()

            this.leaveRates = new LeaveRatesCell({
                readOnly: this.params.readOnly,
                scrollTo: this.params.scrollTo,
                activeLeaveTypes: this.state.activeLeaveTypes,
                onChangeCb: leaveRates => this._handleRatesChanged({leaveRates}),
                rollbackChanges: this.callbacks.rollbackLastEditedUserRateChanges,
                subscribeSelectorOnScroll: this.callbacks.subscribeSelectorOnScroll,
                leaveRatesMenu: this.params.leaveRatesMenu,
            }).init(this.$leaveRatesColumn)

            this.leaveRates.setState({
                regularRate: this.state.rate.regularRate,
                leaveRates: this.state.rate.leaveRates,
                isEmptyValid: isRateEmpty(this.state.rate, this.state.overtimeEnabled, this.state.leavesEnabled)
            })
        }

        this._updateError(this.state.error)
    }

    _handleRegularRateChanged(regularRate) {
        this.callbacks.onChangeCb(this._fillRatesIfNeeded(regularRate))
    }

    _fillRatesIfNeeded(regularRate) {
        const rates = {
            ...this.state.rate,
            regularRate
        }

        if (isValueEmpty(regularRate) || !FloatNumberValidator.getInstance().isValidNumber(regularRate)) {
            return rates
        }

        if (this.state.overtimeEnabled && isValueEmpty(this.state.rate.overtimeRate)) {
            rates.overtimeRate = regularRate * this.state.overtimeRateCoefficient
        }

        if (this.state.leavesEnabled && this.state.activeLeaveTypes.length > 0 && isLeaveRatesEmpty(this.state.rate.leaveRates)) {
            rates.leaveRates = this.state.activeLeaveTypes.map(lt => ({leaveTypeId: lt.id, rate: regularRate * lt.rateCoefficient}))
        }

        return rates
    }

    _handleRatesChanged(changes) {
        this.callbacks.onChangeCb({...this.state.rate, ...changes})
    }

    _updateError(error) {
        this.effectiveDate.setState({
            error: !!error[UserRatesErrors.EFFECTIVE_DATE_EMPTY]
                   ? UserRatesErrors.EFFECTIVE_DATE_EMPTY : !!error[ValidationErrors.DUPLICATED_EFFECTIVE_DATE] ? ValidationErrors.DUPLICATED_EFFECTIVE_DATE : null,
            needShowWarning: error.showWarning === ShowWarning.EFFECTIVE_DATE_WARNING,
        })

        this.regularRate.setState({
            error: !!error[UserRatesErrors.REGULAR_RATE_EMPTY]
                   ? UserRatesErrors.RATE_EMPTY : !!error[UserRatesErrors.REGULAR_RATE_INVALID] ? UserRatesErrors.RATE_INVALID : null,
            needShowWarning: error.showWarning === ShowWarning.REGULAR_RATE_WARNING
        })

        if (this.state.overtimeEnabled) {
            const isEmpty = (isRateValid(this.state.rate.regularRate) && isValueEmpty(this.state.rate.overtimeRate)) || !!error[UserRatesErrors.OVERTIME_RATE_EMPTY]
            this.overtimeRate.setState({
                error: isEmpty ? UserRatesErrors.RATE_EMPTY : !!error[UserRatesErrors.OVERTIME_RATE_INVALID] ? UserRatesErrors.RATE_INVALID : null,
                needShowWarning: error.showWarning === ShowWarning.OVERTIME_RATE_WARNING
            })
        }

        if (this.state.leavesEnabled && this.state.activeLeaveTypes.length > 0) {
            const isEmpty = (isRateValid(this.state.rate.regularRate) && isLeaveRatesEmpty(this.state.rate.leaveRates)) || !!error[UserRatesErrors.LEAVE_RATES_EMPTY]
            this.leaveRates.setState({
                error: isEmpty ? UserRatesErrors.RATE_EMPTY : !!error[UserRatesErrors.LEAVE_RATES_INVALID] ? UserRatesErrors.RATE_INVALID : null,
                needShowWarning: error.showWarning === ShowWarning.LEAVE_RATES_WARNING
            })
        }
    }

    _toggleDeleteConfirmation(show) {
        this.$userRates.toggle(!show)
        this.$deleteConfirmationContainer.toggle(show)

        if (show) {
            new DeleteConfirmationCell({
                deleteMessageKey: 'userlist.userdata.effective_date_row_delete_confirmation',
                onConfirm: () => this.callbacks.onDeleteCb({removePermanently: false}),
                onCancel: () => this._toggleDeleteConfirmation(false)
            }).init(this.find('.deleteConfirmationContainer'))
        } else {
            this.find('.deleteConfirmationContainer').empty()
        }
    }
}


// WEBPACK FOOTER //
// ./web/js/panels/user/accountSettings/userRates/UserRatesRow.js